import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Paginación de listados en cliente, con tamaño de página configurable
 * (10 / 20 / 50) desde el propio formulario del listado.
 *
 * Por qué en cliente y no en servidor: los endpoints de listado de los 4
 * backends devuelven hoy el array completo, sin cursor en el contrato de
 * respuesta. Paginar en servidor implica cambiar ese contrato en los 4
 * repos + este. Esto pagina lo que ya llegó -- resuelve el problema real
 * de UI (tablas de cientos de filas que hay que scrollear entero) sin
 * tocar contratos. Cuando un listado crezca al punto de que traer todo
 * pese en la red, ese listado concreto se migra a cursor en servidor y
 * este hook se reemplaza SOLO ahí.
 *
 * La preferencia de tamaño se recuerda por listado (`storageKey`) en
 * localStorage: quien administra 300 usuarios elige 50 una vez, no en
 * cada visita. localStorage puede fallar (modo privado, cookies
 * bloqueadas) -- si falla, se usa el default y no se rompe nada.
 */
export const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];

const DEFAULT_ROWS_PER_PAGE = 10;
const STORAGE_PREFIX = 'whisperbi.rowsPerPage.';

const readStoredRowsPerPage = (storageKey, fallback) => {
  if (!storageKey) return fallback;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    const parsed = Number.parseInt(raw, 10);
    return ROWS_PER_PAGE_OPTIONS.includes(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const writeStoredRowsPerPage = (storageKey, value) => {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, String(value));
  } catch {
    /* almacenamiento no disponible: la preferencia dura solo esta sesión */
  }
};

/**
 * @param {Array} rows            filas ya cargadas y filtradas
 * @param {Object} [options]
 * @param {string} [options.storageKey]      identificador del listado
 * @param {number} [options.defaultRowsPerPage]
 * @param {Array}  [options.resetDeps]       cuando cambian, vuelve a la página 1
 *                                           (típicamente los filtros del formulario)
 * @param {boolean}  [options.hasMore]       el backend dejó un cursor: quedan
 *                                           filas sin traer
 * @param {boolean}  [options.isLoadingMore] ya hay una petición en vuelo
 * @param {Function} [options.onNeedMore]    se llama al entrar a la última
 *                                           página cargada habiendo más
 */
const useTablePagination = (rows, options = {}) => {
  const {
    storageKey,
    defaultRowsPerPage = DEFAULT_ROWS_PER_PAGE,
    resetDeps = [],
    hasMore = false,
    isLoadingMore = false,
    onNeedMore
  } = options;

  const [rowsPerPage, setRowsPerPageState] = useState(() =>
    readStoredRowsPerPage(storageKey, defaultRowsPerPage)
  );
  const [page, setPage] = useState(0);

  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const count = safeRows.length;

  // Al cambiar un filtro, la página 4 del resultado anterior no significa
  // nada en el resultado nuevo -- se vuelve a la primera.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); }, resetDeps);

  // Si el listado se encogió (se borró una fila, cambió el filtro), la
  // página actual puede quedar fuera de rango. Se acota al derivar en vez
  // de en un efecto, para no renderizar un frame vacío antes de corregir.
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);

  const pageRows = useMemo(() => {
    const start = currentPage * rowsPerPage;
    return safeRows.slice(start, start + rowsPerPage);
  }, [safeRows, currentPage, rowsPerPage]);

  const handleChangePage = useCallback((_event, nextPage) => {
    setPage(nextPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    const next = Number.parseInt(event.target.value, 10);
    setRowsPerPageState(next);
    writeStoredRowsPerPage(storageKey, next);
    setPage(0);
  }, [storageKey]);

  // Al llegar a la última página de lo ya cargado, se le pide al backend la
  // siguiente tanda. Es lo que conecta el pager numerado (que solo entiende
  // de filas en memoria) con el cursor del backend (que no sabe cuántas
  // filas hay en total). El efecto depende de `currentPage`, así que se
  // dispara una sola vez por página aunque el componente se re-renderice.
  const enUltimaPagina = currentPage >= lastPage;
  useEffect(() => {
    if (enUltimaPagina && hasMore && !isLoadingMore && typeof onNeedMore === 'function') {
      onNeedMore();
    }
  }, [enUltimaPagina, hasMore, isLoadingMore, onNeedMore]);

  return {
    page: currentPage,
    rowsPerPage,
    pageRows,
    count,
    /** props listos para <TablePaginationBar {...paginationProps} /> */
    paginationProps: {
      count,
      page: currentPage,
      rowsPerPage,
      onPageChange: handleChangePage,
      onRowsPerPageChange: handleChangeRowsPerPage,
      // El backend pagina con cursor: no sabe -- ni puede saber sin
      // recorrerlo entero -- cuántas filas hay en total. Mientras quede
      // cursor, el total se muestra como "62+".
      hasMore
    }
  };
};

export default useTablePagination;
